import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import emailjs from '@emailjs/browser'
import GlobalIcon from '../assets/icons/global.svg'
import SearchIcon from '../assets/icons/search.svg'
import DocumentIcon from '../assets/icons/document.svg'
import ChartIcon from '../assets/icons/chart.svg'

const heroBg = '/images/hero-world-bg.png'

export default function Home() {
  const { t, getPageTitle, lang } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  })
  const [formStatus, setFormStatus] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    document.title = getPageTitle('home')
  }, [lang, getPageTitle])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setFormStatus(null)

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      company: formData.company,
      subject: lang === 'en' ? 'Home Page Inquiry' : '首页咨询',
      message: formData.message,
      to_email: 'Prime@ccicm.co'
    }

    try {
      await sendEmail(templateParams)
      setFormStatus('success')
      setFormData({ name: '', email: '', phone: '', company: '', message: '' })
    } catch (error) {
      console.error('邮件发送失败:', error)
      setFormStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const sendEmail = async (params) => {
    // 本地开发环境使用 Vite 代理
    const localEmailApi = import.meta.env.VITE_LOCAL_EMAIL_API
    if (localEmailApi) {
      const response = await fetch(localEmailApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new Error('邮件服务不可用')
      }

      return await response.json()
    }

    // 生产环境使用 Cloudflare Worker
    const workerUrl = window.CLOUDFLARE_WORKER_URL
    if (workerUrl) {
      const response = await fetch(workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new Error('邮件服务不可用')
      }

      return await response.json()
    }

    const emailJsPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY

    if (emailJsPublicKey) {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID',
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID',
        params,
        emailJsPublicKey
      )
      return { success: true }
    }

    const subjectLine = lang === 'en'
      ? `Inquiry from ${params.from_name}`
      : `来自 ${params.from_name} 的咨询`

    const mailtoLink = `mailto:Prime@ccicm.co?subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(
      `${lang === 'en' ? 'Name' : '姓名'}: ${params.from_name}\n${lang === 'en' ? 'Email' : '邮箱'}: ${params.from_email}\n${lang === 'en' ? 'Phone' : '电话'}: ${params.phone}\n${lang === 'en' ? 'Company' : '公司'}\n\n${params.message}`
    )}`

    window.open(mailtoLink, '_blank')
    return { success: true }
  }

  return (
    <>
      {/* Hero Section */}
      <section className="hero hero-with-bg">
        <div className="hero-bg-image">
          <img src={heroBg} alt="International business services" />
          <div className="hero-overlay"></div>
        </div>
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="fade-in">
              {t('home.hero.title')}<span>{t('home.hero.titleHighlight')}</span>{t('home.hero.titleEnd')}
            </h1>
            <p className="fade-in">
              {t('home.hero.description')}
            </p>
            <div className="hero-buttons fade-in">
              <Link to="/contact" className="btn btn-gold">
                {t('home.hero.freeConsultation')}
              </Link>
              <Link to="/services" className="btn btn-outline">
                {t('home.hero.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services section">
        <div className="container">
          <div className="section-header">
            <h2>{t('home.services.title')}</h2>
            <p>{t('home.services.subtitle')}</p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <img src={GlobalIcon} alt="Global" />
              </div>
              <h3>{t('services.categories.crossBorder')}</h3>
              <p>
                {t('services.list.crossBorderDebtCollection')}
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <img src={SearchIcon} alt="Search" />
              </div>
              <h3>{t('services.categories.dueDiligence')}</h3>
              <p>
                {t('services.list.overseasCreditInvestigation')}
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <img src={DocumentIcon} alt="Document" />
              </div>
              <h3>{t('services.categories.compliance')}</h3>
              <p>
                {t('services.list.odiFiling')}
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <img src={ChartIcon} alt="Finance" />
              </div>
              <h3>{t('services.categories.finance')}</h3>
              <p>
                {t('services.list.supplyChainFinance')}
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/services" className="btn btn-primary">
              {t('home.services.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="about section section-bg">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>{t('home.about.title')}</h2>
              <p>
                {t('home.about.description1')}
              </p>
              <p>
                {t('home.about.description2')}
              </p>
              <div className="about-stats">
                <div className="stat">
                  <span className="stat-number">15+</span>
                  <span className="stat-label">{t('home.about.stats.years')}</span>
                </div>
                <div className="stat">
                  <span className="stat-number">500+</span>
                  <span className="stat-label">{t('home.about.stats.cases')}</span>
                </div>
                <div className="stat">
                  <span className="stat-number">98%</span>
                  <span className="stat-label">{t('home.about.stats.satisfaction')}</span>
                </div>
                <div className="stat">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">{t('home.about.stats.countries')}</span>
                </div>
              </div>
            </div>
            <div className="about-image">
              <div className="about-image-content">
                <img
                  src="/images/about-team.png"
                  alt="Professional team"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ background: 'var(--primary)', color: 'white', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ color: 'white', marginBottom: '16px' }}>
            {t('home.cta.title')}
          </h2>
          <p style={{ color: 'var(--text-light)', marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px' }}>
            {t('home.cta.subtitle')}
          </p>
          <Link to="/contact" className="btn btn-gold">
            {t('home.cta.button')}
          </Link>
        </div>
      </section>
    </>
  )
}
